import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Download, X, Bell } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { enableNotifications, setupNotificationListeners } from '../lib/oneSignalService';

// Pages where the PWA banner should NOT appear (auth/setup flows)
const HIDDEN_PATHS = ['/complete-profile', '/role-selection', '/sign-up', '/auth/callback', '/check-email', '/reset-password', '/forgot-password', '/terms'];

// Check if PWA features are available (not blocked by in-app browsers)
const isPWASupported = () => {
  try {
    // Test localStorage availability
    localStorage.setItem('__pwa_test__', '1');
    localStorage.removeItem('__pwa_test__');

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) return false;

    // Check if Notification API is available
    if (typeof Notification === 'undefined') return false;

    return true;
  } catch (e) {
    return false;
  }
};

const PWAInstallBanner = ({ profile }) => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isEnablingNotifications, setIsEnablingNotifications] = useState(false);
  // NEW: Add delay state for install prompt - prevents showing too quickly after notifications
  const [showInstallReady, setShowInstallReady] = useState(false);
  const [installDelayTimer, setInstallDelayTimer] = useState(null);

  useEffect(() => {
    // Don't show if PWA features are not supported (in-app browsers, etc.)
    if (!isPWASupported()) {
      console.log('[PWABanner] PWA not supported in this environment');
      return;
    }

    // Show on mobile/tablet devices and when user is logged in
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    // Check if already dismissed in this session
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem('pwa-banner-dismissed');
    } catch (e) {
      // sessionStorage blocked
    }

    // Check notification status
    const enabled = typeof Notification !== 'undefined' && Notification.permission === 'granted';
    setNotificationsEnabled(enabled);

    // Setup notification listeners
    setupNotificationListeners((payload) => {
      toast.info(payload.title || 'New notification', {
        description: payload.body
      });
    });

    // Show banner on mobile/tablet devices
    if (isMobileOrTablet && !isStandalone && profile && !dismissed) {
      setShowBanner(true);

      // Auto-dismiss after 10 seconds if not interacted with
      const autoDismissTimer = setTimeout(() => {
        setShowBanner(prev => {
          if (prev) {
            try { sessionStorage.setItem('pwa-banner-dismissed', 'true'); } catch {}
          }
          return false;
        });
      }, 10000);

      return () => {
        clearTimeout(autoDismissTimer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
        if (installDelayTimer) clearTimeout(installDelayTimer);
      };
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      toast.success('Bohol Trabaho app installed successfully!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      // Clean up any pending timer
      if (installDelayTimer) {
        clearTimeout(installDelayTimer);
      }
    };
  }, [profile, installDelayTimer]);

  const handleEnableNotifications = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔔 Enable Notifications button clicked (OneSignal)');
    setIsEnablingNotifications(true);
    
    try {
      if (!('Notification' in window)) {
        toast.error('Notifications are not supported on this browser.');
        return;
      }
      
      // Check if permission is already denied
      if (Notification.permission === 'denied') {
        showManualNotificationInstructions();
        setIsEnablingNotifications(false);
        return;
      }
      
      // Enable OneSignal notifications
      const result = await enableNotifications();
      
      if (result.success) {
        toast.success('🔔 Notifications enabled successfully!');
        setNotificationsEnabled(true);
        
        // NEW: Add 5 second delay before showing install prompt
        // This ensures the browser's beforeinstallprompt has fired
        console.log('📱 Starting 5 second delay before showing install option...');
        const timer = setTimeout(() => {
          console.log('📱 Delay complete, showing install option');
          setShowInstallReady(true);
        }, 5000);
        setInstallDelayTimer(timer);
      } else {
        // Check if it was blocked by popup blocker or denied
        if (Notification.permission === 'denied') {
          showManualNotificationInstructions();
        } else if (result.error?.includes('blocked') || result.error?.includes('popup')) {
          // Popup blocker detected
          showManualNotificationInstructions();
        } else {
          toast.error('Failed: ' + result.error);
        }
      }
    } catch (error) {
      console.error('[OneSignal] Notification request error:', error);
      // If error occurred, it might be popup blocker
      if (Notification.permission === 'default') {
        showManualNotificationInstructions();
      } else {
        toast.error('Failed to enable notifications. Please try again.');
      }
    } finally {
      setIsEnablingNotifications(false);
    }
  };

  // Show manual instructions for enabling notifications when popup is blocked
  const showManualNotificationInstructions = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isChrome = /Chrome/i.test(navigator.userAgent);
    const isSamsung = /SamsungBrowser/i.test(navigator.userAgent);
    
    let instructions = '';
    
    if (isAndroid) {
      if (isSamsung) {
        instructions = `📱 Samsung Browser Instructions:

1. Tap the ⋮ menu (3 dots)
2. Go to "Settings" → "Sites and downloads"
3. Tap "Notifications"
4. Find this site and tap "Allow"
5. Return here and tap "Enable" again`;
      } else if (isChrome) {
        instructions = `📱 Chrome Instructions:

1. Tap the 🔒 lock icon in the address bar
2. Tap "Permissions" or "Site settings"
3. Find "Notifications" and tap "Allow"
4. Return here and tap "Enable" again`;
      } else {
        instructions = `📱 To Enable Notifications:

1. Open your browser settings
2. Find "Site Settings" or "Permissions"
3. Look for "Notifications"
4. Allow notifications for this site
5. Return here and tap "Enable" again`;
      }
    } else if (isIOS) {
      instructions = `📱 iOS Instructions:

Note: iOS requires the app to be installed first.

1. Tap "Install" below to add the app
2. Open the installed app
3. Notifications will be enabled automatically`;
    } else {
      instructions = `🔔 Notifications Blocked

Your browser blocked the notification prompt.

To enable:
1. Click the lock/info icon in the address bar
2. Find "Notifications" in permissions
3. Change to "Allow"
4. Refresh the page`;
    }
    
    toast.info(instructions, { 
      duration: 15000,
      style: { whiteSpace: 'pre-line', textAlign: 'left' }
    });
  };

  const handleInstallClick = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('📱 Install App button clicked');
    setIsInstalling(true);
    
    try {
      if (deferredPrompt) {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('✅ User accepted the PWA install prompt');
          toast.success('Installing Bohol Trabaho app...');
          setShowBanner(false);
        } else {
          console.log('❌ User dismissed the PWA install prompt');
          toast.info('Installation cancelled');
        }
        
        setDeferredPrompt(null);
      } else {
        console.warn('⚠️ No install prompt available');
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        
        if (isStandalone) {
          toast.info('App is already installed!');
          setShowBanner(false);
        } else {
          // Show manual install instructions based on browser/device
          showManualInstallInstructions();
        }
      }
    } catch (error) {
      console.error('Error during installation:', error);
      // If prompt failed, show manual instructions
      showManualInstallInstructions();
    } finally {
      setIsInstalling(false);
    }
  };

  // Show manual instructions for installing the app when prompt is blocked or unavailable
  const showManualInstallInstructions = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isChrome = /Chrome/i.test(navigator.userAgent);
    const isSamsung = /SamsungBrowser/i.test(navigator.userAgent);
    const isFirefox = /Firefox/i.test(navigator.userAgent);
    
    let instructions = '';
    
    if (isIOS) {
      instructions = `📱 Install on iPhone/iPad:

1. Tap the Share button (□↑) at the bottom
2. Scroll down and tap "Add to Home Screen"
3. Tap "Add" in the top right
4. Find the app icon on your home screen`;
    } else if (isAndroid) {
      if (isSamsung) {
        instructions = `📱 Samsung Browser Install:

1. Tap the ☰ menu (3 lines) at the bottom
2. Tap "Add page to" → "Home screen"
3. Tap "Add" to confirm
4. Find the app icon on your home screen`;
      } else if (isChrome) {
        instructions = `📱 Chrome Install:

1. Tap the ⋮ menu (3 dots) in the top right
2. Tap "Add to Home screen" or "Install app"
3. Tap "Add" or "Install" to confirm
4. Find the app icon on your home screen`;
      } else if (isFirefox) {
        instructions = `📱 Firefox Install:

1. Tap the ⋮ menu (3 dots)
2. Tap "Install" or "Add to Home screen"
3. Follow the prompts to add
4. Find the app icon on your home screen`;
      } else {
        instructions = `📱 Install the App:

1. Open your browser's menu (⋮ or ☰)
2. Look for "Add to Home screen" or "Install"
3. Follow the prompts to install
4. Find the app icon on your home screen`;
      }
    } else {
      // Desktop
      instructions = `💻 Install on Desktop:

1. Look for the install icon (⊕) in the address bar
2. Or click the browser menu → "Install app"
3. Click "Install" to confirm`;
    }
    
    toast.info(instructions, { 
      duration: 15000,
      style: { whiteSpace: 'pre-line', textAlign: 'left' }
    });
  };

  const handleDismiss = () => {
    setShowBanner(false);
    try { sessionStorage.setItem('pwa-banner-dismissed', 'true'); } catch {}
  };

  if (!showBanner) {
    return null;
  }

  // Hide on auth/setup pages — only show on dashboard and browsing pages
  if (HIDDEN_PATHS.some(path => location.pathname.startsWith(path))) {
    return null;
  }

  // Check if notifications are denied by browser
  const notificationsDenied = 'Notification' in window && Notification.permission === 'denied';

  return (
    <div className="fixed bottom-[4.5rem] left-2 right-2 z-50 md:hidden">
      <Alert className="bg-gradient-to-r from-primary to-primary/80 text-white border-primary shadow-lg rounded-xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {notificationsEnabled && showInstallReady ? (
              <>
                <Download size={20} className="flex-shrink-0" />
                <AlertDescription className="text-white font-medium m-0 text-sm">
                  Get the full app experience!
                </AlertDescription>
              </>
            ) : notificationsEnabled && !showInstallReady ? (
              <>
                <Download size={20} className="flex-shrink-0 animate-pulse" />
                <AlertDescription className="text-white font-medium m-0 text-sm">
                  Getting install ready...
                </AlertDescription>
              </>
            ) : notificationsDenied ? (
              <>
                <Bell size={20} className="flex-shrink-0" />
                <AlertDescription className="text-white font-medium m-0 text-xs">
                  Notifications blocked. Tap lock icon 🔒 in address bar → Permissions → Allow
                </AlertDescription>
              </>
            ) : (
              <>
                <Bell size={20} className="flex-shrink-0" />
                <AlertDescription className="text-white font-medium m-0 text-sm">
                  Enable notifications & install app
                </AlertDescription>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {!notificationsEnabled ? (
              <>
                <Button
                  onClick={handleEnableNotifications}
                  disabled={isEnablingNotifications}
                  size="sm"
                  variant="secondary"
                  className="rounded-xl font-semibold whitespace-nowrap text-xs"
                  data-testid="pwa-enable-notifications-button"
                >
                  <Bell size={14} className="mr-1" />
                  {isEnablingNotifications ? 'Enabling...' : 'Enable'}
                </Button>
                {/* Help button for users having trouble with popup blockers */}
                <Button
                  onClick={showManualNotificationInstructions}
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20 rounded-xl text-xs px-2"
                  title="Having trouble? Tap for help"
                >
                  ?
                </Button>
              </>
            ) : showInstallReady ? (
              <>
                <Button
                  onClick={handleInstallClick}
                  disabled={isInstalling}
                  size="sm"
                  variant="secondary"
                  className="rounded-xl font-semibold whitespace-nowrap text-xs"
                  data-testid="pwa-install-button"
                >
                  <Download size={14} className="mr-1" />
                  {isInstalling ? 'Installing...' : 'Install'}
                </Button>
                {/* Show help button if no deferred prompt (popup might be blocked) */}
                {!deferredPrompt && (
                  <Button
                    onClick={showManualInstallInstructions}
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20 rounded-xl text-xs px-2"
                    title="Installation help"
                  >
                    ?
                  </Button>
                )}
              </>
            ) : (
              // Show "Preparing..." while waiting for the 5-second delay
              <Button
                disabled
                size="sm"
                variant="secondary"
                className="rounded-xl font-semibold whitespace-nowrap text-xs opacity-70"
              >
                <Download size={14} className="mr-1 animate-pulse" />
                Preparing...
              </Button>
            )}
            
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 rounded-xl p-2 h-auto"
              data-testid="pwa-dismiss-button"
            >
              <X size={18} />
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default PWAInstallBanner;
